import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
    Search, Bell, ChevronDown, MessageSquare, User, Mail, MessageCircle,
    Sparkles, Command, Menu, X, LifeBuoy, Sun, Moon, Settings, LogOut,
} from 'lucide-react';
import { useTranslation } from "react-i18next";
import useImportModuleLabels from "@/hooks/useImportModuleLabels";
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useDebounced } from '@/hooks/useDebounced';

import { useTheme } from '../../context/ThemeContext';
import Chat from '../chat/Chat';
import NotificationCenter from '../notifications/NotificationCenter';
import LanguageSelector from './LanguageSelector';
import HelpTicketModal from '../helpdesk/HelpTicketModal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { baseURL } from '@/config/APIConfig';
import { clearTmsBrand } from '@/utils/tmsDocumentTitle';
import { prefetchRoute } from '@/utils/routePrefetch';
import { clearStorageKeepingDevice, getSessionUserId, sessionLogout } from '@/services/sessionClient';
import { endSession } from '@/utility/authSession';

const API = baseURL.dashboardApi;
const LOCAL_API = baseURL.local;
const BACKEND_URL = baseURL.live || '';

const SEARCH_ROUTE_GROUPS = [
    {
        label: 'Core',
        items: [
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Modules', path: '/modules' },
            { label: 'Forwarder Dashboard', path: '/forwarder-dashboard' },
            { label: 'Operations Dashboard', path: '/operations-dashboard' },
            { label: 'Purchase Orders', path: '/purchase-orders' },
            { label: 'Sales Orders', path: '/sales-orders' },
            { label: 'Load Plan', path: '/load-plan' },
            { label: 'Shipment List', path: '/ShipmentList' },
            { label: 'Vision Board', path: '/vision-board' },
            { label: 'Mailbox', path: '/mailbox' },
            { label: 'Email Summary', path: '/email-summary' },
            { label: 'Rate Matrix', path: '/ratematrix' },
            { label: 'Ocean Control Tower', path: '/ocean-control-tower' },
            { label: 'Notifications', path: '/notifications' },
        ],
    },
    {
        label: 'Booking',
        items: [
            { label: 'Booking List', path: '/booking' },
            { label: 'New Booking', path: '/booking/new' },
            { label: 'Cancel Booking', path: '/booking/cancel' },
            // { label: 'Booking Print', path: '/booking/print/:id' },
            // { label: 'Booking PDF Demo', path: '/booking/pdf-demo/:id' },
            { label: 'Address Book', path: '/address-book' },
        ],
    },
    {
        label: 'Booking Requests',
        items: [
            { label: 'Booking Request List', path: '/booking-request-list' },
            { label: 'New Booking Request', path: '/booking-request/new' },
            // { label: 'Booking Request Print', path: '/booking-request/print' },
        ],
    },
    {
        label: 'AI & Insights',
        items: [
            { label: 'Rate Intelligence', path: '/rate-agent' },
            { label: 'Quotes / Bookings', path: '/quotes-bookings' },
            { label: 'AI Agent Setup', path: '/ai-agent-setup' },
            { label: 'AI Agent Dashboard', path: '/ai-agent-dashboard' },
            { label: 'Agent Control Tower', path: '/agent-control-tower' },
            { label: 'AI Agent Reports', path: '/ai-agent-reports' },
        ],
    },
    {
        label: 'Reports',
        items: [
            { label: 'Quotation Report', path: '/quotation-report' },
            { label: 'Booking Report', path: '/booking-report' },
            { label: 'Accounting Report', path: '/accounting-report' },
            { label: 'Import Quotation Report', path: '/import-quotation-report' },
            { label: 'Import Booking Report', path: '/import-booking-report' },
            { label: 'Air Export Quotation Report', path: '/air/export/quotation-report' },
            { label: 'Air Import Quotation Report', path: '/air/import/quotation-report' },
            { label: 'Air Export Booking Report', path: '/air/export/booking-report' },
            { label: 'Air Import Booking Report', path: '/air/import/booking-report' },
            { label: 'Domestic Export Quotation Report', path: '/domestic/export/quotation-report' },
            { label: 'Domestic Import Quotation Report', path: '/domestic/import/quotation-report' },
            { label: 'Domestic Export Booking Report', path: '/domestic/export/booking-report' },
            { label: 'Domestic Import Booking Report', path: '/domestic/import/booking-report' },
            { label: 'Ocean Export Reports', path: '/ocean/export/reports' },
            { label: 'Ocean Import Reports', path: '/ocean/import/reports' },
            { label: 'Air Export Reports', path: '/air/export/reports' },
            { label: 'Air Import Reports', path: '/air/import/reports' },
            { label: 'Domestic Export Reports', path: '/domestic/export/reports' },
            { label: 'Domestic Import Reports', path: '/domestic/import/reports' },
        ],
    },
    {
        label: 'Operations',
        items: [
            { label: 'Customs Filing', path: '/customs' },
            { label: 'Container Tracking', path: '/containers' },
            { label: 'Financials', path: '/financials' },
            { label: 'Port / Place', path: '/port-place' },
            { label: 'Terminal Mapping', path: '/terminal-mapping' },
            { label: 'Maintain Locations', path: '/maintain-locations' },
            { label: 'Users', path: '/Users' },
            { label: 'Maintain Vessel', path: '/maintain-vessel' },
            { label: 'Commodity', path: '/Commodity' },
            { label: 'Country Clause', path: '/CountryClause' },
            { label: 'Clauses', path: '/Clause' },
            { label: 'Charges Code', path: '/ChargesCode' },
        ],
    },
    {
        label: 'Sales & Documents',
        items: [
            { label: 'Rate Quote', path: '/rate-quote' },
            { label: 'New Rate Quote', path: '/rate-quote/new' },
            { label: 'HBL', path: '/hbl' },
            { label: 'MBL', path: '/mbl' },
            { label: 'Status Audit', path: '/status-audit' },
            { label: 'RFQ', path: '/rfq' },
            { label: 'Mail Compose', path: '/mail/compose' },
        ],
    },
    {
        label: 'Accounting',
        items: [
            { label: 'Accounting Home', path: '/accounting' },
            { label: 'Chart of Accounts', path: '/accounting/chart-of-accounts' },
            { label: 'Accounting Charge Codes', path: '/accounting/charge-codes' },
            { label: 'Customer Invoices', path: '/accounting/invoices' },
            { label: 'Vendor Bills', path: '/accounting/bills' },
            { label: 'Journal Entries', path: '/accounting/journals' },
            { label: 'Payments', path: '/accounting/payments' },
            { label: 'Accounting Reports', path: '/accounting/reports' },
        ],
    },
    {
        label: 'Ocean',
        items: [
            { label: 'Import Rate Quote', path: '/import-rate-quote' },
            { label: 'New Import Rate Quote', path: '/import-rate-quote/new' },
            { label: 'Import Booking', path: '/import-booking' },
            { label: 'New Import Booking', path: '/import-booking/new' },
            { label: 'Release Order', path: '/release-order' },
            { label: 'New Release Order', path: '/release-order/new' },
            { label: 'Delivery Order', path: '/delivery-order/new' },
            // { label: 'Import Booking Status Audit', path: '/import-booking/status-audit/:id' },
            // { label: 'Import HBL Print', path: '/import-hbl-print/:bookingId' },
            // { label: 'Import ISF Print', path: '/import-isf-print/:bookingId' },
            { label: 'Ocean Schedule', path: '/ocean/schedule' },
            { label: 'Ocean Tracking', path: '/ocean/tracking' },
        ],
    },
    {
        label: 'Air Freight',
        items: [
            { label: 'Air Dashboard', path: '/air/dashboard' },
            { label: 'Air Export Shipments', path: '/air/export/shipments' },
            { label: 'Air Export Rate Quotes', path: '/air/export/rate-quotes' },
            { label: 'Air Export Rate Quote', path: '/air/export/rate-quote/new' },
            { label: 'Air Export Cancel Quotes', path: '/air/export/cancel-quotes' },
            { label: 'Air Export Cancel Shipments', path: '/air/export/cancel-shipments' },
            { label: 'Air Export Booking', path: '/air/export/booking' },
            { label: 'Air Export Online Booking', path: '/air/export/online-bookings' },
            { label: 'Air Import Booking', path: '/air/import/booking' },
            { label: 'Air Import Shipments', path: '/air/import/shipments' },
            { label: 'Air Import Rate Quotes', path: '/air/import/rate-quotes' },
            { label: 'Air Import Rate Quote', path: '/air/import/rate-quote/new' },
            { label: 'Air Import Cancel Quotes', path: '/air/import/cancel-quotes' },
            { label: 'Air Import Cancel Shipments', path: '/air/import/cancel-shipments' },
            { label: 'Air Import Pre Alert', path: '/air/import/pre-alert' },
            { label: 'Air Import Arrival Notice', path: '/air/import/arrival-notice' },
            { label: 'Air Import Customs', path: '/air/import/customs' },
            { label: 'Air Import Delivery Order', path: '/air/import/delivery-order' },
            { label: 'Air Import Cargo Delivery', path: '/air/import/cargo-delivery' },
            { label: 'Air Import POD', path: '/air/import/pod' },
            // { label: 'Air Status Audit', path: '/air/import/status-audit/:id' },
            { label: 'Air Tracking', path: '/air/tracking' },
        ],
    },
    {
        label: 'Domestic',
        items: [
            { label: 'Domestic Export Rate Quotes', path: '/domestic/export/rate-quotes' },
            { label: 'Domestic Export Work Orders', path: '/domestic/export/work-orders' },
            { label: 'Domestic Import Rate Quotes', path: '/domestic/import/rate-quotes' },
            { label: 'Domestic Import Work Orders', path: '/domestic/import/work-orders' },
            { label: 'Domestic Export Reports', path: '/domestic/export/reports' },
            { label: 'Domestic Import Reports', path: '/domestic/import/reports' },
        ],
    },
    {
        label: 'Misc',
        items: [
            { label: 'Quote Response', path: '/quote-response' },
            { label: 'Admin Login', path: '/adminLogin' },
        ],
    },
];

const buildSearchGroups = (groups) => {
    let nextIndex = 0;
    return groups.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
            ...item,
            globalIndex: nextIndex++,
        })),
    }));
};

const INITIAL_SEARCH_GROUPS = buildSearchGroups(SEARCH_ROUTE_GROUPS);
const INITIAL_SEARCH_RESULTS = INITIAL_SEARCH_GROUPS.flatMap((group) => group.items);

// ─── Modern Theme Toggle ──────────────────────────────────────────────────────
const ThemeToggle = ({ theme, onToggle }) => {
    const isDark = theme === 'dark';

    return (
        <button
            onClick={onToggle}
            data-testid="theme-toggle"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className={`
                group flex items-center gap-2 h-9 px-3 rounded-xl
                border transition-all duration-200 select-none outline-none flex-shrink-0
                ${isDark
                    ? 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'bg-white/60 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700/60 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover:text-slate-900 dark:hover:text-white'
                }
            `}
        >
            <motion.span
                key={isDark ? 'moon' : 'sun'}
                initial={{ rotate: -30, opacity: 0, scale: 0.7 }}
                animate={{ rotate: isDark ? 15 : 0, opacity: 1, scale: 1 }}
                exit={{ rotate: 30, opacity: 0, scale: 0.7 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className={`flex-shrink-0 transition-colors duration-200
                    ${isDark ? 'text-indigo-400' : 'text-amber-500 group-hover:text-amber-600'}`}
            >
                {isDark
                    ? <Moon className="w-[15px] h-[15px]" />
                    : <Sun className="w-[15px] h-[15px]" />
                }
            </motion.span>

            <span className={`
                hidden md:block text-[11px] font-bold tracking-widest uppercase
                ${isDark
                    ? 'text-slate-400 dark:text-slate-500'
                    : 'text-slate-400 dark:text-slate-500'
                }
            `}>
                {isDark ? 'Dark' : 'Light'}
            </span>
        </button>
    );
};

// ─── Icon Button ──────────────────────────────────────────────────────────────
const IconBtn = ({ onClick, testId, ariaLabel, badge, children }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        aria-label={ariaLabel}
        className="relative w-9 h-9 rounded-lg
                   bg-slate-100 dark:bg-slate-800
                   text-slate-500 dark:text-slate-400
                   hover:bg-slate-200 dark:hover:bg-slate-700
                   hover:text-slate-900 dark:hover:text-white
                   transition-all duration-150 flex items-center justify-center flex-shrink-0"
    >
        {children}
        {badge > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px]
                             bg-red-500 text-white text-[10px] font-bold
                             rounded-full flex items-center justify-center px-1 ring-2
                             ring-white dark:ring-slate-900">
                {badge > 9 ? '9+' : badge}
            </span>
        )}
    </button>
);

// ─── TopBar ───────────────────────────────────────────────────────────────────
const TopBar = ({ onChatToggle, onMenuToggle, isSidebarOpen }) => {
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { moduleLabel } = useImportModuleLabels();

    const [emailCount, setEmailCount] = useState(0);
    const [newEmails, setNewEmails] = useState([]);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [unreadChatCount, setUnreadChatCount] = useState(0);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchSections, setSearchSections] = useState([]);
    const [flatSearchResults, setFlatSearchResults] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [showNotificationCenter, setShowNotificationCenter] = useState(false);
    const [notificationCount, setNotificationCount] = useState(0);
    const [showHelpDesk, setShowHelpDesk] = useState(false);
    const inputRef = useRef(null);

    // Ref to track when we are intentionally navigating — prevents
    // the onBlur setTimeout from re-opening or interfering with state.
    const isNavigatingRef = useRef(false);

    // ── Reset search state completely whenever the route changes ─────────────
    // This is the key fix: after navigate() fires and the URL changes,
    // we wipe all search state so the next focus starts fresh.
    useEffect(() => {
        isNavigatingRef.current = false;
        setIsSearchFocused(false);
        setSearchQuery('');
        setSearchSections([]);
        setFlatSearchResults([]);
        setHighlightedIndex(0);
    }, [location.pathname]);

    // ── Search: only runs when query is non-empty ─────────────────────────────
    const updateSearchResults = useDebounced((value) => {
        const query = value.trim().toLowerCase();

        // If empty — clear results, show nothing
        if (!query) {
            setSearchSections([]);
            setFlatSearchResults([]);
            setHighlightedIndex(0);
            return;
        }

        let nextIndex = 0;
        const sections = SEARCH_ROUTE_GROUPS.reduce((acc, group) => {
            const matches = group.items
                .map((item) => {
                    const label = item.label.toLowerCase();
                    const path = item.path.toLowerCase();
                    const labelScore = label.startsWith(query) ? 0 : label.includes(query) ? 1 : 3;
                    const pathScore = path.includes(query) ? 0 : 2;
                    const score = Math.min(labelScore, pathScore) * 10 + Math.abs(labelScore - pathScore);
                    return { ...item, score, groupLabel: group.label };
                })
                .filter((item) => item.score < 20)
                .sort((a, b) => a.score - b.score || a.label.localeCompare(b.label));

            if (matches.length > 0) {
                acc.push({
                    ...group,
                    items: matches.map((item) => ({ ...item, globalIndex: nextIndex++ })),
                });
            }
            return acc;
        }, []);

        const flatResults = sections.flatMap((section) => section.items);
        setSearchSections(sections);
        setFlatSearchResults(flatResults);
        setHighlightedIndex(0);
    }, 200);

    const navigateToSuggestion = (item) => {
        if (!item) return;
        // Mark as navigating BEFORE blur so the onBlur handler
        // knows to skip its closeSearch call.
        isNavigatingRef.current = true;
        // Blur the input so virtual keyboards close on mobile.
        try { inputRef.current?.blur(); } catch (_) { /* ignore */ }
        // State will be cleared by the location.pathname useEffect
        // once navigate() causes a route change.
        navigate(item.path);
    };

    const closeSearch = () => {
        setIsSearchFocused(false);
        setSearchSections([]);
        setFlatSearchResults([]);
        setHighlightedIndex(0);
    };

    const handleSearchInput = (event) => {
        const nextValue = event.target.value;
        setSearchQuery(nextValue);
        updateSearchResults(nextValue);
    };

    const handleSearchKeyDown = (event) => {
        if (flatSearchResults.length === 0) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((current) => Math.min(current + 1, flatSearchResults.length - 1));
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex((current) => Math.max(current - 1, 0));
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            const selected = flatSearchResults[highlightedIndex];
            if (selected) navigateToSuggestion(selected);
        }

        if (event.key === 'Escape') {
            closeSearch();
        }
    };

    const highlightMatch = (text) => {
        const term = searchQuery.trim();
        if (!term) return text;
        const lowerText = text.toLowerCase();
        const lowerTerm = term.toLowerCase();
        const index = lowerText.indexOf(lowerTerm);
        if (index === -1) return text;

        return (
            <>
                {text.slice(0, index)}
                <span className="font-semibold text-slate-900 dark:text-white">
                    {text.slice(index, index + term.length)}
                </span>
                {text.slice(index + term.length)}
            </>
        );
    };

    // ── Auth ──────────────────────────────────────────────────────────────────
    // localStorage can be "", "null", or truncated — never crash TopBar on parse.
    const readLocalJsonArray = (key) => {
        try {
            const raw = localStorage.getItem(key);
            if (raw == null || String(raw).trim() === "") return [{}];
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed.length > 0 ? parsed : [{}];
            if (parsed && typeof parsed === "object") return [parsed];
            return [{}];
        } catch {
            return [{}];
        }
    };
    const [authUser] = readLocalJsonArray("authUser");
    const [MasterCompanyDetails] = readLocalJsonArray("MasterCompanyDetails");
    const _first = authUser?.firstName || authUser?.FirstName || '';
    const _last = authUser?.lastName || authUser?.LastName || '';
    const _full = `${_first} ${_last}`.trim();
    const userName =
        _full ||
        authUser?.userName ||
        authUser?.UserName ||
        authUser?.username ||
        authUser?.email?.split('@')[0] ||
        'User';
    const userInitials = userName.slice(0, 2).toUpperCase();

    const _logout = async () => {
        // Close the tracked session first, otherwise the backend keeps it
        // "active" and the next sign-in reports a false device conflict.
        try {
            const userId = getSessionUserId(authUser);
            if (userId && userId !== 'unknown') await sessionLogout(userId);
        } catch (_) { /* ignore — sign out locally regardless */ }

        // This tab navigates away immediately, so only the other tabs need the
        // signal — skipping the local event avoids flashing the notice dialog.
        endSession({ reason: 'logout', notifyLocal: false });

        clearTmsBrand();
        clearStorageKeepingDevice();
        window.location.href = '/';
    };

    const roleList = [
        { "ROLE_ID": 1, "ROLE_NAME": "SUPER ADMIN" },
        { "ROLE_ID": 2, "ROLE_NAME": "COMPANY ADMIN" },
        { "ROLE_ID": 3, "ROLE_NAME": "OPERATION USER" },
        { "ROLE_ID": 4, "ROLE_NAME": "MANAGER" },
        { "ROLE_ID": 5, "ROLE_NAME": "DATA ENTRY" },
        { "ROLE_ID": 6, "ROLE_NAME": "VIEWER" },
        { "ROLE_ID": 7, "ROLE_NAME": "ACCOUNTS" },
    ];

    const roleName = roleList.find(role => role.ROLE_ID === authUser?.roleID)?.ROLE_NAME || 'User';
    const companyName = MasterCompanyDetails?.CompanyName;

    // ── Notification polling ──────────────────────────────────────────────────
    useEffect(() => {
        const fetchNotificationCount = async () => {
            try {
                const response = await axios.get(`${API}/notifications?limit=1`);
                setNotificationCount(response.data.unread_count || 0);
            } catch {
                setNotificationCount(6);
            }
        };
        fetchNotificationCount();
        const interval = setInterval(fetchNotificationCount, 30000);
        return () => clearInterval(interval);
    }, []);

    // ── Unread chat polling ───────────────────────────────────────────────────
    useEffect(() => {
        const checkUnreadMessages = async () => {
            try {
                const currentUser = localStorage.getItem('username') || 'demo-user';
                const lastChecked = localStorage.getItem('chat_last_checked') || new Date(0).toISOString();
                const channelsResponse = await axios.get(`${API}/chat/channels`);
                const channels = channelsResponse.data.channels || [];
                let totalUnread = 0;
                for (const channel of channels) {
                    try {
                        const messagesResponse = await axios.get(`${API}/chat/messages/${channel.id}`);
                        const messages = messagesResponse.data.messages || [];
                        totalUnread += messages.filter(
                            (msg) => new Date(msg.timestamp) > new Date(lastChecked) && msg.sender !== currentUser
                        ).length;
                    } catch (err) { /* ignore per-channel errors */ }
                }
                setUnreadChatCount(totalUnread);
            } catch { /* ignore */ }
        };
        checkUnreadMessages();
        const interval = setInterval(checkUnreadMessages, 30000);
        return () => clearInterval(interval);
    }, []);

    // ── Email polling ─────────────────────────────────────────────────────────
    useEffect(() => {
        checkNewEmails();
        const interval = setInterval(checkNewEmails, 120000);
        return () => clearInterval(interval);
    }, []);

    const checkNewEmails = async () => {
        try {
            const response = await axios.get(`${API}/mailbox/check-new`);
            if (response.data.new_emails > 0 && response.data.new_emails > emailCount) {
                const newCount = response.data.new_emails - emailCount;
                if (emailCount > 0) {
                    toast.success(`${newCount} new email${newCount > 1 ? 's' : ''} received!`, {
                        icon: <Mail className="w-5 h-5" />,
                    });
                }
            }
            setEmailCount(response.data.new_emails);
            setNewEmails(response.data.emails);
        } catch { /* ignore */ }
    };

    // Derived: is there an active query?
    const hasQuery = searchQuery.trim().length > 0;
    // Derived: should the dropdown panel be visible?
    const showDropdown = isSearchFocused && hasQuery;
    // Derived: should the empty-state hint be visible?
    const showEmptyHint = isSearchFocused && !hasQuery;

    return (
        <>
            <motion.header
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl
                       border-b border-slate-200 dark:border-slate-800
                       sticky top-0 z-40"
                data-testid="topbar"
            >
                <div className="h-14 flex items-center px-3 sm:px-4 lg:px-6 gap-3">

                    {/* ── Hamburger (mobile only) ── */}
                    <button
                        onClick={onMenuToggle}
                        className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0
                               bg-slate-100 dark:bg-slate-800
                               text-slate-500 dark:text-slate-400
                               hover:bg-slate-200 dark:hover:bg-slate-700
                               hover:text-slate-900 dark:hover:text-white transition-colors"
                        aria-label="Toggle menu"
                    >
                        {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>

                    {/* ── Search ── */}
                    <div className="relative flex-1 max-w-xl group">
                        {/* Glow border on hover */}
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 via-[#00C6FB] to-blue-600
                                    rounded-xl opacity-0 group-hover:opacity-100 blur
                                    transition-all duration-500 hidden sm:block pointer-events-none" />

                        {/* Input row */}
                        <div className="relative flex items-center
                                    bg-slate-100 dark:bg-slate-800
                                    border border-slate-200 dark:border-slate-700
                                    rounded-xl overflow-hidden
                                    group-hover:shadow-sm transition-all duration-200">
                            <div className="w-10 flex items-center justify-center flex-shrink-0">
                                <Search className="w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                            </div>
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchQuery}
                                placeholder="Search pages & modules…"
                                data-testid="global-search"
                                onFocus={() => {
                                    // Only open search if we're not in the middle of a navigation
                                    if (!isNavigatingRef.current) {
                                        setIsSearchFocused(true);
                                    }
                                }}
                                onBlur={() => {
                                    // If we're navigating, skip — location change useEffect will clean up.
                                    if (isNavigatingRef.current) return;
                                    setTimeout(() => {
                                        // Guard again in case navigation started during the timeout
                                        if (isNavigatingRef.current) return;
                                        if (document.activeElement !== inputRef.current) {
                                            closeSearch();
                                        }
                                    }, 150);
                                }}
                                onChange={handleSearchInput}
                                onKeyDown={handleSearchKeyDown}
                                className="flex-1 bg-transparent py-2.5 pr-2 text-sm
                                       text-slate-900 dark:text-white
                                       placeholder-slate-400 dark:placeholder-slate-500
                                       border-none focus:outline-none focus:ring-0 min-w-0"
                            />
                            <div className="hidden sm:flex items-center gap-2 pr-3 flex-shrink-0">
                                <div className="flex items-center gap-1 px-2 py-0.5
                                            bg-blue-50 dark:bg-blue-900/30
                                            border border-blue-100 dark:border-blue-800
                                            rounded-md">
                                    <Sparkles className="w-3 h-3 text-blue-500" />
                                    <span className="text-[11px] font-semibold text-blue-600 dark:text-blue-400">AI</span>
                                </div>
                                <div className="hidden md:flex items-center gap-0.5 px-1.5 py-0.5
                                            bg-slate-200 dark:bg-slate-700 rounded-md">
                                    <Command className="w-3 h-3 text-slate-400" />
                                    <span className="text-[11px] text-slate-400 font-medium">K</span>
                                </div>
                            </div>
                        </div>

                        <AnimatePresence>
                            {/* ── Empty-state hint: focused but nothing typed ── */}
                            {showEmptyHint && (
                                <motion.div
                                    key="search-empty-hint"
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute left-0 right-0 mt-2 rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 shadow-xl shadow-slate-900/10 z-50 px-6 py-8 flex flex-col items-center gap-3"
                                >
                                    <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                        <Search className="w-5 h-5 text-slate-400 dark:text-slate-500" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Search pages &amp; modules
                                        </p>
                                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                            Type a page or module name — results appear instantly
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3 mt-1">
                                        <kbd className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                                            ↑ ↓
                                        </kbd>
                                        <span className="text-[11px] text-slate-400">navigate</span>
                                        <kbd className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                                            Enter
                                        </kbd>
                                        <span className="text-[11px] text-slate-400">open</span>
                                        <kbd className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                                            Esc
                                        </kbd>
                                        <span className="text-[11px] text-slate-400">close</span>
                                    </div>
                                </motion.div>
                            )}

                            {/* ── Results dropdown: only when user has typed ── */}
                            {showDropdown && (
                                <motion.div
                                    key="search-results"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.15 }}
                                    className="absolute left-0 right-0 mt-2 max-h-[360px] overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 shadow-xl shadow-slate-900/10 z-50"
                                >
                                    {/* Header */}
                                    <div className="px-4 pt-4 pb-3 border-b border-slate-200 dark:border-slate-800">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                                    Search pages &amp; modules
                                                </div>
                                                <div className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                                                    {flatSearchResults.length > 0
                                                        ? `${flatSearchResults.length} result${flatSearchResults.length === 1 ? '' : 's'} for "${searchQuery}"`
                                                        : `No results for "${searchQuery}"`
                                                    }
                                                </div>
                                            </div>
                                            <div className="text-[11px] text-slate-400 dark:text-slate-500">
                                                Enter to open
                                            </div>
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className="max-h-[300px] overflow-auto p-4 space-y-4">
                                        {searchSections.length === 0 ? (
                                            /* No matches */
                                            <div className="flex flex-col items-center gap-3 py-8">
                                                <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                                    <Search className="w-5 h-5 text-slate-300 dark:text-slate-600" />
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                                        No matching routes found
                                                    </p>
                                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                                        Try a different name or path keyword
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            searchSections.map((section) => (
                                                <div key={section.label} className="space-y-2">
                                                    {/* Section label */}
                                                    <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                                        <span>{section.label}</span>
                                                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                                            {section.items.length}
                                                        </span>
                                                    </div>
                                                    {/* Items */}
                                                    <div className="grid gap-1">
                                                        {section.items.map((item) => (
                                                            <button
                                                                key={`${item.path}-${item.globalIndex}`}
                                                                type="button"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onMouseEnter={() => prefetchRoute(item.path)}
                                                                onFocus={() => prefetchRoute(item.path)}
                                                                onClick={() => navigateToSuggestion(item)}
                                                                className={`w-full text-left rounded-2xl px-3 py-2 transition duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500
                                                                    ${highlightedIndex === item.globalIndex
                                                                        ? 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white'
                                                                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                                                                    }`}
                                                            >
                                                                <div className="font-medium">
                                                                    {highlightMatch(
                                                                        item.path.startsWith("/import-")
                                                                            ? moduleLabel(item.label)
                                                                            : item.label,
                                                                    )}
                                                                </div>
                                                                <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                                                                    {highlightMatch(item.path)}
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* ── Right actions ── */}
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ml-auto">

                        {/* AI Chat */}
                        <IconBtn onClick={onChatToggle} testId="ai-chat-toggle" ariaLabel="AI assistant">
                            <MessageSquare className="w-[18px] h-[18px]" />
                        </IconBtn>

                        <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 mx-0.5" />

                        {/* Help Desk */}
                        <IconBtn onClick={() => setShowHelpDesk(true)} testId="help-desk-toggle" ariaLabel="Help Desk">
                            <LifeBuoy className="w-[18px] h-[18px]" />
                        </IconBtn>

                        {/* Team Chat */}
                        <IconBtn
                            onClick={() => setIsChatOpen(true)}
                            testId="team-chat-toggle"
                            ariaLabel="Team chat"
                            badge={unreadChatCount}
                        >
                            <MessageCircle className="w-[18px] h-[18px]" />
                        </IconBtn>

                        {/* Notifications */}
                        <div className="relative flex-shrink-0">
                            <IconBtn
                                onClick={() => setShowNotificationCenter((v) => !v)}
                                testId="notifications-btn"
                                ariaLabel="Notifications"
                                badge={notificationCount}
                            >
                                <Bell className="w-[18px] h-[18px]" />
                            </IconBtn>
                            <NotificationCenter
                                isOpen={showNotificationCenter}
                                onClose={() => setShowNotificationCenter(false)}
                                onUnreadCountChange={setNotificationCount}
                            />
                        </div>

                        {/* Language */}
                        <LanguageSelector />

                        {/* Theme */}
                        <ThemeToggle theme={theme} onToggle={toggleTheme} />

                        <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 flex-shrink-0" />

                        {/* User Profile */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    data-testid="user-profile-btn"
                                    className="group flex items-center gap-2 pl-1 pr-1.5 py-1 rounded-lg
                                           hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0
                                           outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                                >
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center
                                                bg-gradient-to-br from-blue-600 to-[#00C6FB]
                                                text-white text-[11px] font-bold tracking-wide
                                                ring-2 ring-transparent group-data-[state=open]:ring-blue-400/40 transition-shadow">
                                        {userInitials}
                                    </div>
                                    <div className="hidden md:block max-w-[120px] leading-tight text-left">
                                        <div className="truncate text-[12px] font-semibold text-slate-800 dark:text-slate-100">
                                            {userName}
                                        </div>

                                        <div
                                            className="truncate text-[10px] text-slate-400 dark:text-slate-500"
                                            title={companyName}
                                        >
                                            {companyName}
                                        </div>
                                    </div>
                                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden md:block flex-shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <div className="flex items-center gap-3 px-2.5 py-2.5 mb-0.5">
                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-[#00C6FB] text-[11px] font-bold text-white">
                                        {userInitials}
                                    </div>
                                    <div className="min-w-0 leading-tight">
                                        <p className="truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">
                                            {userName}
                                        </p>
                                        <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">
                                            {roleName}
                                        </p>
                                    </div>
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                                <DropdownMenuItem className="text-slate-700 dark:text-slate-200">
                                    <User className="text-slate-400" />
                                    Profile
                                </DropdownMenuItem>
                                <DropdownMenuItem className="text-slate-700 dark:text-slate-200">
                                    <Settings className="text-slate-400" />
                                    Settings
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={_logout}
                                    className="text-rose-600 focus:bg-rose-50 focus:text-rose-700 dark:text-rose-400 dark:focus:bg-rose-500/10 dark:focus:text-rose-300 cursor-pointer"
                                >
                                    <LogOut className="opacity-80" />
                                    Logout
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                </div>
            </motion.header>

            {/* ── Modals ── */}
            <Chat
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                onUnreadCountChange={setUnreadChatCount}
            />
            <HelpTicketModal
                isOpen={showHelpDesk}
                onClose={() => setShowHelpDesk(false)}
            />
        </>
    );
};

export default TopBar;